<script lang="ts">
export default {
  name: "MainFobizzNavbarMyFobizzOffcanvas",
};
</script>

<script setup lang="ts">
import { useSession } from "@/composables/useSession";

import MyFobizzMenuHeader from "./MyFobizzMenuHeader.vue";
import MyFobizzMenuLink from "./MyFobizzMenuLink.vue";

import mySchoolIcon from "/images/Meine_Schule_Icon.svg";
import mySchoolLicenseIcon from "/images/Meine_Schullizenz_Icon.svg";
import logoUrlMobile from "/images/fobizz-logo-F.png";

const userStore = useUserStore();

const { t } = useI18n();
const { tools, courses } = useUserProfileStatus();

const { logout } = useSession();

const handleLogout = async () => {
  await logout();
};
</script>

<template>
  <span
    v-tooltip="{
      triggers: ['hover'],
      placement: 'bottom',
      content: t('mainFobizzNavbar.tooltips.myfobizz'),
    }"
  >
    <MainFobizzNavbarIconButton
      style="font-size: 1.5rem"
      icon-name="person"
      data-bs-toggle="offcanvas"
      data-bs-target="#navbar-offcanvas"
      aria-controls="navbar-offcanvas"
      data-testid="button-myfobizz-offcanvas"
      :label="t('mainFobizzNavbar.tooltips.myfobizz')"
    />
  </span>
  <div
    id="navbar-offcanvas"
    class="offcanvas offcanvas-end my-fobizz-offcanvas"
    tabindex="-1"
    aria-labelledby="navbar-offcanvasLabel"
  >
    <div class="offcanvas-header border-bottom px-4">
      <img
        class="d-flex align-items-center d-lg-none"
        :src="logoUrlMobile"
        width="35"
        alt="fobizz logo"
      />
      <h5
        id="navbar-offcanvasLabel"
        class="offcanvas-title my-fobizz-offcanvas__headline d-none d-lg-flex"
      >
        {{ t("mainFobizzNavbar.myfobizz.headers.myfobizz") }}
      </h5>
      <button
        type="button"
        class="btn-close text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      ></button>
    </div>
    <div class="offcanvas-body p-0 d-flex flex-column">
      <div class="my-fobizz-offcanvas__menu">
        <div class="my-fobizz-offcanvas__menu__section">
          <div class="my-fobizz-offcanvas__menu__links two-columns">
            <MyFobizzMenuLink
              :title="t('mainFobizzNavbar.myfobizz.links.contact')"
              route-to="help-redirect"
              icon-name="question-circle"
            />
            <MyFobizzMenuLink
              v-if="!userStore.user?.international"
              :title="t('mainFobizzNavbar.myfobizz.links.news')"
              href="/posts"
              :render-as-link="true"
              data-click-tracking="true"
            >
              <MainFobizzNavbarNotificationIcon />
              {{ t("mainFobizzNavbar.myfobizz.links.news") }}
            </MyFobizzMenuLink>
          </div>
        </div>
        <hr class="my-fobizz-offcanvas__menu__divider" />
        <div class="my-fobizz-offcanvas__menu__section">
          <MyFobizzMenuHeader
            :title="t('mainFobizzNavbar.myfobizz.headers.settings')"
          />
          <div class="my-fobizz-offcanvas__menu__links two-columns">
            <MyFobizzMenuLink
              :title="t('mainFobizzNavbar.myfobizz.links.profile')"
              href="/profile/general"
              icon-name="gear"
              :render-as-link="true"
            />

            <MyFobizzMenuLink
              :title="t('mainFobizzNavbar.myfobizz.links.subscriptions')"
              href="/profile_subscription"
              icon-name="ticket"
              :render-as-link="true"
            />

            <MyFobizzMenuLink
              :title="t('mainFobizzNavbar.myfobizz.links.school')"
              href="/schule"
              :render-as-link="true"
              data-click-tracking="true"
            >
              <img
                width="16"
                :src="mySchoolIcon"
                class="responsive-image"
                alt=""
              />
              {{ t("mainFobizzNavbar.myfobizz.links.school") }}
            </MyFobizzMenuLink>

            <MyFobizzMenuLink
              :title="t('mainFobizzNavbar.myfobizz.links.schoolLicense')"
              href="/schule"
              :render-as-link="true"
              data-click-tracking="true"
            >
              <img
                width="16"
                :src="mySchoolLicenseIcon"
                class="responsive-image"
                alt=""
              />
              {{ t("mainFobizzNavbar.myfobizz.links.schoolLicense") }}
            </MyFobizzMenuLink>
          </div>
          <hr class="my-fobizz-offcanvas__menu__divider" />
          <div class="my-fobizz-offcanvas__menu__section">
            <MyFobizzMenuHeader
              :title="t('mainFobizzNavbar.myfobizz.headers.courses')"
            >
              <span
                v-if="courses.license && !userStore.user?.international"
                v-tooltip.bottom="courses.tooltip"
                class="badge rounded-pill bg-brand-1"
                style="cursor: pointer"
                data-click-tracking="true"
              >
                {{ courses.label }}
              </span>
            </MyFobizzMenuHeader>

            <div class="my-fobizz-offcanvas__menu__links">
              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.courses')"
                icon-name="mortarboard"
                href="/course_enrollments?tab=doing"
                :render-as-link="true"
              />

              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.histories')"
                icon-name="patch-check"
                href="/course_enrollments?tab=done"
                :render-as-link="true"
              />

              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.courseBookmarks')"
                icon-name="heart"
                href="/course_bookmarks"
                :render-as-link="true"
                data-click-tracking="true"
              />

              <MyFobizzMenuLink
                v-if="userStore.additionalQualificationUrl"
                :title="
                  t('mainFobizzNavbar.myfobizz.links.additionalQualification')
                "
                icon-name="award"
                :href="userStore.additionalQualificationUrl"
                :render-as-link="true"
              />

              <MyFobizzMenuLink
                v-if="userStore.user?.admin || userStore.platformUser?.author"
                :title="t('mainFobizzNavbar.myfobizz.links.manageCourses')"
                icon-name="book"
                href="/author/courses"
                :render-as-link="true"
              />
            </div>
          </div>

          <hr class="my-fobizz-offcanvas__menu__divider" />

          <div class="my-fobizz-offcanvas__menu__section">
            <MyFobizzMenuHeader
              :title="t('mainFobizzNavbar.myfobizz.headers.tools')"
            >
              <span
                v-if="tools.license && !userStore.user?.international"
                v-tooltip.bottom="tools.tooltip"
                class="badge rounded-pill bg-primary"
                style="cursor: pointer"
                data-click-tracking="true"
              >
                {{ tools.label }}
              </span>
            </MyFobizzMenuHeader>

            <div class="my-fobizz-offcanvas__menu__links">
              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.folders')"
                icon-name="folder"
                route-to="folders-index"
                data-click-tracking="true"
              />

              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.classes')"
                icon-name="people"
                route-to="teacher-school-class-index"
                data-click-tracking="true"
              />

              <MyFobizzMenuLink
                :title="t('mainFobizzNavbar.myfobizz.links.materialsBookmarks')"
                icon-name="heart"
                route-to="bookmark-index"
                data-click-tracking="true"
              />
            </div>
          </div>

          <template
            v-if="userStore.user?.admin || userStore.isPlatformReviewer"
          >
            <hr class="my-fobizz-offcanvas__menu__divider" />
            <div class="my-fobizz-offcanvas__menu__section">
              <MyFobizzMenuHeader
                :title="t('mainFobizzNavbar.myfobizz.headers.adminArea')"
              />
              <div class="my-fobizz-offcanvas__menu__links">
                <MyFobizzMenuLink
                  v-if="userStore.user?.admin"
                  :title="
                    t('mainFobizzNavbar.myfobizz.links.backofficePlatform')
                  "
                  href="/backoffice"
                  :render-as-link="true"
                />
                <MyFobizzMenuLink
                  v-if="userStore.isPlatformReviewer"
                  :title="t('mainFobizzNavbar.myfobizz.links.reviewerSection')"
                  href="/reviewer"
                  :render-as-link="true"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="my-fobizz-offcanvas__menu my-fobizz-offcanvas__menu__footer">
        <div class="my-fobizz-offcanvas__menu__section">
          <div class="my-fobizz-offcanvas__menu__links">
            <button
              class="btn btn-link w-100 my-fobizz-offcanvas__menu__link-item"
              data-turbo="false"
              type="button"
              data-testid="button-myfobizz-menu-logout"
              @click="handleLogout"
            >
              <BaseIcon
                icon-name="box-arrow-right"
                color="inherit"
                size="inherit"
              />
              {{ t("links.logout") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- // Global Styles shared in nested components, align with plattform -->
<style lang="scss">
.my-fobizz-offcanvas {
  // Check to overwrite bootstrap offcanvas width globally after redesign
  --#{$prefix}offcanvas-width: 450px !important;

  @include media-breakpoint-down(sm) {
    --#{$prefix}offcanvas-width: 400px !important;
  }

  background-color: $color-white;

  &__headline {
    color: $body-color;
    font-weight: $font-weight-semibold;
    font-size: $h4-font-size;
  }

  &__menu {
    color: $body-color;
    padding-top: $spacer-4;
    padding-bottom: $spacer-4;
    display: grid;
    gap: $spacer-2;

    &__section {
      display: grid;
      gap: $spacer-2;
    }

    &__header {
      font-size: $font-size-xs;
      font-weight: $font-weight-semibold;
      display: flex;
      flex-wrap: wrap;
      gap: $spacer-2;
      align-items: center;
      justify-content: space-between;
      padding-left: $spacer-4;
      padding-right: $spacer-4;

      .badge {
        font-size: $font-size-xxs;
      }
    }

    &__links.two-columns {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 0;

      @include media-breakpoint-down(sm) {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
      }
    }

    &__link-item {
      font-size: $font-size-base;
      color: $body-color;
      text-decoration: none;
      display: flex;
      gap: $spacer-2;
      align-items: center;
      padding-left: $spacer-4;
      padding-top: $spacer-2;
      padding-bottom: $spacer-2;

      &:hover {
        color: $body-color;
        background-color: $gray-100;
      }
    }

    &__divider {
      margin-top: $spacer-2;
      margin-bottom: $spacer-2;
      opacity: 1;
      border-top: 2px solid $border-color;
    }

    &__footer {
      background-color: $gray-100;
      flex: 1;
      align-items: baseline;
    }

    &__footer &__link-item:hover {
      background-color: $gray-200;
    }
  }
}
</style>
